import { css } from '@emotion/react';
import {
  borderRadius,
  colorSets,
  IconUnmarked,
  IconVisibilityOn,
  List,
  spacing,
  styled,
  TextElement,
} from '@lessonup/ui-components';
import React from 'react';

interface Props {
  revealed: boolean;
  handleReveal?: (e: React.MouseEvent) => void;
  ariaLabel: string;
  answers: string[];
}

export function AnswerCardContentAnswer({ revealed, handleReveal, answers, ariaLabel }: Props) {
  const isRevealable = !!handleReveal;

  return (
    <StyledAnswerWrapper
      aria-label={ariaLabel}
      onClick={handleReveal}
      as={isRevealable ? 'button' : undefined}
      tabIndex={isRevealable ? 0 : undefined}
      $revealed={revealed}
      $revealable={isRevealable}
      $hasAnswers={!!answers.length && revealed}
    >
      {isRevealable && !revealed && <StyledIconEye />}
      {!isRevealable && !revealed && <StyledIconQuestionMark />}
      {answers.length > 1 ? (
        <List>
          {answers.map((answer, index) => (
            <StyledCorrectAnswer key={`answer-${index}`} textStyle="bodyText" size="small" revealed={revealed} as="li">
              {answer}
            </StyledCorrectAnswer>
          ))}
        </List>
      ) : (
        <StyledAnswerText textStyle="bodyText" size="small" revealed={revealed}>
          {answers[0]}
        </StyledAnswerText>
      )}
    </StyledAnswerWrapper>
  );
}

const StyledAnswerWrapper = styled.div<{ $revealed: boolean; $revealable: boolean; $hasAnswers: boolean }>`
  cursor: ${({ $revealable }) => ($revealable ? 'pointer' : 'auto')};
  border-radius: ${borderRadius.rounded4};
  padding: ${spacing.size0} ${spacing.size4};
  ${({ $revealed, $revealable, $hasAnswers }) => {
    if (!$revealable)
      return css`
        border: 2px dashed ${colorSets.active.stroke};
        display: flex;
        align-items: center;
        justify-content: ${$hasAnswers ? 'flex-start' : 'center'};
      `;

    return !$revealed
      ? css`
          display: flex;
          align-items: center;
          justify-content: center;
          background: ${colorSets.active.fill};
          color: ${colorSets.active.on};
          border: 2px solid ${colorSets.active.stroke};

          &:hover {
            background: ${colorSets.active.fillFeedback};
          }
        `
      : css`
          border: 2px dashed ${colorSets.active.stroke};
          text-align: left;

          &:hover {
            background: ${colorSets.neutral.fillFeedback};
          }
        `;
  }}
`;

const iconStyle = `
  position: absolute;
  user-select: none;
  width: 16px;
  height: 16px;
`;

const StyledIconEye = styled(IconVisibilityOn)`
  ${iconStyle}
`;

const StyledIconQuestionMark = styled(IconUnmarked)`
  ${iconStyle}
`;

const StyledAnswerText = styled(TextElement)<{ revealed: boolean }>`
  opacity: ${({ revealed }) => (revealed ? 1 : 0)};
  user-select: none;
  white-space: pre-wrap;
`;

const StyledCorrectAnswer = styled(TextElement)<{ revealed: boolean }>`
  opacity: ${({ revealed }) => (revealed ? 1 : 0)};
  user-select: none;
  white-space: pre-wrap;
  margin-left: 1em;
`;
